import { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Button,
  Typography,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRails, submitRailDataAction } from '../store/rails.actions';
import {
  selectRails,
  selectRailsStatus,
  selectSubmitStatus,
  selectSubmitError,
} from '../store/rails.selectors';
import { resetSubmitStatus } from '../store/rails.slice';
import { RequestStatus } from '../../../utils/enums';

// ============ Styled Components ============

const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  height: 'calc(100vh - 160px)',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
  fontFamily: theme.typography.fontFamily,
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    height: 'calc(100vh - 140px)',
    flexDirection: 'column',
  },
}));

const VideoSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  position: 'relative',
  minHeight: 0,
  [theme.breakpoints.down('sm')]: {
    minHeight: 'auto',
    gap: theme.spacing(1),
  },
}));

// Overlay panel that sits on top of the video, holding the rail-type buttons.
const VideoOverlay = styled(Box)(({ theme, position = 'left' }) => ({
  position: 'absolute',
  alignContent: 'center',
  [position]: 0,
  width: '160px',
  height: '65vh',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  borderRadius:
    position === 'left'
      ? `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`
      : `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
  overflowY: 'auto',
  zIndex: 10,
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: '2px',
  },
  [theme.breakpoints.down('xl')]: {
    width: '145px',
    height: '55vh',
    padding: theme.spacing(1.25),
    gap: theme.spacing(0.75),
  },
  [theme.breakpoints.down('lg')]: {
    width: '130px',
    padding: theme.spacing(1),
    gap: theme.spacing(0.5),
    height: '50vh',
  },
  [theme.breakpoints.down(1050)]: {
    width: '120px',
    padding: theme.spacing(0.9),
    gap: theme.spacing(0.4),
    height: '45vh',
  },
  [theme.breakpoints.down('md')]: {
    width: '110px',
    padding: theme.spacing(0.75),
    gap: theme.spacing(0.3),
    height: '40vh',
  },
  [theme.breakpoints.down(750)]: {
    width: '100px',
    padding: theme.spacing(0.6),
    gap: theme.spacing(0.2),
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const VideoPlayerCard = styled(Card)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundColor: '#000',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  position: 'relative',
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const VideoInfoBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(1),
    gap: theme.spacing(1),
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0.75),
    gap: theme.spacing(0.75),
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

// Overlay-styled button for each rail type — mirrors LineTypeButton from the
// lane marking recorder so the two tools feel consistent.
const RailTypeButton = styled(Button)(
  ({ theme, selected = false, isRecording = false, overlay = false }) => ({
    padding: overlay ? theme.spacing(0.75) : theme.spacing(1.25),
    fontSize: overlay ? '0.75rem' : '0.7rem',
    textTransform: 'none',
    fontWeight: 600,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    minHeight: overlay ? '50px' : '70px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `2px solid ${selected ? theme.palette.primary.main : overlay ? 'rgba(255,255,255,0.3)' : theme.palette.divider}`,
    backgroundColor: selected
      ? '#C8102E !important'
      : isRecording
        ? theme.palette.warning.light
        : overlay
          ? 'rgba(255,255,255,0.1)'
          : theme.palette.background.paper,
    color: selected ? '#fff' : overlay ? '#fff' : theme.palette.text.primary,
    boxShadow: selected ? theme.shadows[4] : 'none',
    '&:hover': {
      backgroundColor: selected
        ? 'rgba(102, 126, 234, 0.9)'
        : overlay
          ? 'rgba(255,255,255,0.2)'
          : theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
      transform: overlay ? 'scale(1.03)' : 'translateY(-2px)',
      boxShadow: theme.shadows[6],
    },
    '&:active': {
      transform: overlay ? 'scale(0.98)' : 'translateY(0)',
    },
    [theme.breakpoints.down('xl')]: {
      padding: overlay ? theme.spacing(0.65) : theme.spacing(1.1),
      fontSize: overlay ? '0.68rem' : '0.68rem',
      minHeight: overlay ? '48px' : '68px',
    },
    [theme.breakpoints.down('lg')]: {
      padding: overlay ? theme.spacing(0.5) : theme.spacing(1),
      fontSize: overlay ? '0.64rem' : '0.65rem',
      minHeight: overlay ? '45px' : '60px',
    },
    [theme.breakpoints.down(1050)]: {
      padding: overlay ? theme.spacing(0.45) : theme.spacing(0.85),
      fontSize: overlay ? '0.6rem' : '0.62rem',
      minHeight: overlay ? '42px' : '55px',
    },
    [theme.breakpoints.down('md')]: {
      padding: overlay ? theme.spacing(0.4) : theme.spacing(0.75),
      fontSize: overlay ? '0.58rem' : '0.6rem',
      minHeight: overlay ? '40px' : '50px',
    },
    [theme.breakpoints.down(750)]: {
      padding: overlay ? theme.spacing(0.35) : theme.spacing(0.65),
      fontSize: overlay ? '0.54rem' : '0.55rem',
      minHeight: overlay ? '36px' : '45px',
    },
  })
);

const DataListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(1.5),
  height: '100%',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.divider,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  minWidth: '320px',
  maxWidth: '420px',
  height: '100%',
  overflow: 'hidden',
  [theme.breakpoints.down('lg')]: {
    minWidth: '280px',
    maxWidth: '340px',
  },
  [theme.breakpoints.down('md')]: {
    minWidth: '250px',
    maxWidth: '300px',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
    maxWidth: '100%',
    height: 'auto',
    maxHeight: '300px',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const RecordingIndicator = styled(Box)(({ theme, isRecording = false }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isRecording ? '#ffebee' : '#f5f5f5',
  border: `1px solid ${isRecording ? '#ffcdd2' : theme.palette.divider}`,
}));

// ============ Utility Functions ============

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '00:00';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const timeStringToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const [minutes, seconds] = timeStr.split(':').map(Number);
  return (minutes || 0) * 60 + (seconds || 0);
};

const validateTimeFormat = (timeStr) => {
  if (!timeStr) return false;
  const regex = /^\d{1,2}:\d{2}$/;
  if (!regex.test(timeStr)) return false;
  const [, seconds] = timeStr.split(':').map(Number);
  return seconds >= 0 && seconds < 60;
};

// Rail types shown in the LEFT overlay panel (shortcut: A + 1..7)
const railTypes = [
  { id: 1, name: '3 Strand Cable Barrier', shortcut: '1' },
  { id: 2, name: 'Box Beam Barrier', shortcut: '2' },
  { id: 3, name: 'W-Beam Barrier', shortcut: '3' },
  { id: 4, name: 'Concrete NJ Shape ', shortcut: '4' },
  { id: 5, name: 'Single Slope Barrier', shortcut: '5' },
  { id: 6, name: 'Three Beam Barrier', shortcut: '6' },
  { id: 7, name: 'Timber Barrier', shortcut: '7' },
];

const buildEventsFromRails = (railsArray) => {
  if (!Array.isArray(railsArray)) return [];

  return railsArray.flatMap((rail, railIndex) => {
    let railData = rail.railjson || rail.rail_json || rail.rail || rail;
    if (typeof railData === 'string') {
      try {
        railData = JSON.parse(railData);
      } catch (error) {
        return [];
      }
    }
    if (!railData || typeof railData !== 'object') return [];
    railData = railData.rail || railData;

    const referenceTimes = Object.values(railData)
      .flatMap((event) => [event?.start_time, event?.end_time])
      .filter((value) => typeof value === 'string' && value.includes('T'))
      .map((value) => new Date(value).getTime())
      .filter((value) => Number.isFinite(value));
    const referenceTime = referenceTimes.length > 0 ? Math.min(...referenceTimes) : null;

    return Object.entries(railData)
      .filter(([, event]) => event && typeof event === 'object' && event.start_time !== undefined)
      .map(([key, event], eventIndex) => {
        const railType = railTypes.find((type) => type.name === event.type);
        const parseTime = (value) => {
          if (typeof value === 'number') return value > 1000 ? value / 1000 : value;
          if (typeof value === 'string' && value.includes('T')) {
            const timestamp = new Date(value).getTime();
            return referenceTime ? (timestamp - referenceTime) / 1000 : timestamp / 1000;
          }
          if (typeof value === 'string' && value.includes(':')) return timeStringToSeconds(value);
          const parsed = Number(value);
          return Number.isFinite(parsed) ? (parsed > 1000 ? parsed / 1000 : parsed) : 0;
        };
        const startTime = parseTime(event.start_time);
        const endTime = parseTime(event.end_time);

        return {
          id: `rail_${rail.id || rail.railid || railIndex}_${key || eventIndex}`,
          type: event.type,
          height: event.height || railType?.height,
          postSpacing: event.postSpacing || event.spacing || railType?.spacing,
          deflection: event.deflection || railType?.deflection,
          startTime,
          endTime,
          duration: endTime - startTime,
          status: 'finished',
        };
      });
  });
};

// Try to load selected drive info from localStorage (in case of page refresh)
const storedRow = localStorage.getItem('selectedDriveRow');
let row = null;
try {
  row = storedRow ? JSON.parse(storedRow) : null;
} catch (e) {
  row = null;
}

// ============ Main Component ============

const RailMarkingRecorder = ({
  driveId = row?.id,
  driveName = row?.name || 'demo-drive',
  videoUrl = null || row?.videoUrl,
  currentTime = 0,
  userId = null,
  onEventsChange = null,
}) => {
  // State for rail markings
  const [railEvents, setRailEvents] = useState([]);
  const [initialRailEvents, setInitialRailEvents] = useState([]);
  const [hasLoadedExistingRails, setHasLoadedExistingRails] = useState(false);
  const dispatch = useDispatch();

  // Recording state — tracks which rail type button is currently "on"
  const [recordingRailType, setRecordingRailType] = useState(null);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [shortcutModalOpen, setShortcutModalOpen] = useState(false);

  // Editing state
  const [editingEventId, setEditingEventId] = useState(null);
  const [editStartTime, setEditStartTime] = useState('');
  const [editEventType, setEditEventType] = useState(null);
  const [editTimeError, setEditTimeError] = useState('');
  const [localCurrentTime, setLocalCurrentTime] = useState(0);

  // Selectors for submit status
  const submitStatus = useSelector(selectSubmitStatus);
  const submitError = useSelector(selectSubmitError);
  const rails = useSelector(selectRails);
  const railsStatus = useSelector(selectRailsStatus);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // Video ref for accessing current time
  const videoRef = useRef(null);

  useEffect(() => {
    if (driveId) dispatch(fetchRails(driveId));
  }, [dispatch, driveId]);

  useEffect(() => {
    setHasLoadedExistingRails(false);
  }, [driveId]);

  useEffect(() => {
    if (railsStatus !== RequestStatus.SUCCEEDED || hasLoadedExistingRails) return;

    const loadedEvents = buildEventsFromRails(rails);
    setRailEvents(loadedEvents);
    setInitialRailEvents(loadedEvents);
    setHasLoadedExistingRails(true);
  }, [rails, railsStatus, hasLoadedExistingRails]);

  // Handle submit status changes
  useEffect(() => {
    if (submitStatus === RequestStatus.SUCCEEDED) {
      setInitialRailEvents(railEvents);
      setShowSuccessAlert(true);
      const timer = setTimeout(() => {
        setShowSuccessAlert(false);
        // Reset submit status to allow new submissions
        dispatch(resetSubmitStatus());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus, dispatch, railEvents]);

  // Handle submit error changes
  useEffect(() => {
    if (submitStatus === RequestStatus.FAILED && submitError) {
      setShowErrorAlert(true);
      const timer = setTimeout(() => {
        setShowErrorAlert(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus, submitError]);

  // Reset submit status when component unmounts (when leaving page)
  useEffect(() => {
    return () => {
      dispatch(resetSubmitStatus());
    };
  }, [dispatch]);

  // Get current video time
  const getCurrentVideoTime = () => {
    return videoRef.current?.currentTime || localCurrentTime || 0;
  };

  // Handle rail type button click — same start/stop/switch pattern used by
  // the lane marking recorder's overlay line-type buttons.
  const handleRailTypeClick = useCallback(
    (railType) => {
      const videoTime = getCurrentVideoTime();

      if (recordingRailType?.id === railType.id) {
        // Stop recording this rail type
        const newEvent = {
          id: `rail_${Date.now()}`,
          type: railType.name,
          height: railType.height,
          postSpacing: railType.spacing,
          deflection: railType.deflection,
          startTime: recordingStartTime,
          endTime: videoTime,
          duration: videoTime - recordingStartTime,
          status: 'finished',
        };
        setRailEvents((prev) => [...prev, newEvent]);
        setRecordingRailType(null);
        setRecordingStartTime(null);
      } else {
        // If recording a different rail type, close it out first
        if (recordingRailType) {
          const stopEvent = {
            id: `rail_${Date.now()}`,
            type: recordingRailType.name,
            height: recordingRailType.height,
            postSpacing: recordingRailType.spacing,
            deflection: recordingRailType.deflection,
            startTime: recordingStartTime,
            endTime: videoTime,
            duration: videoTime - recordingStartTime,
            status: 'finished',
          };
          setRailEvents((prev) => [...prev, stopEvent]);
        }
        // Start recording the newly clicked rail type
        setRecordingRailType(railType);
        setRecordingStartTime(videoTime);
      }
    },
    [recordingRailType, recordingStartTime]
  );

  // Keyboard shortcuts: A + 1..6 for the left panel
  useEffect(() => {
    let pendingShortcutSide = null;
    let timeoutId = null;

    const handleShortcutKeyDown = (event) => {
      const key = event.key.toLowerCase();
      if (key === 'a') {
        pendingShortcutSide = 'left';
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
          pendingShortcutSide = null;
        }, 1200);
        return;
      }
      if (pendingShortcutSide && /^[1-6]$/.test(key)) {
        const railType = railTypes.find((rt) => rt.shortcut === key);
        if (railType) {
          handleRailTypeClick(railType);
          event.preventDefault();
        }
        pendingShortcutSide = null;
        clearTimeout(timeoutId);
      }
    };

    window.addEventListener('keydown', handleShortcutKeyDown);
    return () => {
      window.removeEventListener('keydown', handleShortcutKeyDown);
      clearTimeout(timeoutId);
    };
  }, [handleRailTypeClick]);

  // Edit event handlers
  const handleOpenEdit = (event) => {
    setEditingEventId(event.id);
    setEditStartTime(formatTime(event.startTime));
    setEditEventType(event);
  };

  const handleSaveEdit = () => {
    if (!editEventType) return;

    // Validate time format
    if (!validateTimeFormat(editStartTime)) {
      setEditTimeError('Invalid time format. Use MM:SS (e.g., 01:30)');
      return;
    }

    const startSeconds = timeStringToSeconds(editStartTime);
    const endSeconds = getCurrentVideoTime();

    // Validate time range
    if (startSeconds >= endSeconds) {
      setEditTimeError('Start time must be before end time');
      return;
    }

    const updateEventInList = (list) =>
      list.map((event) =>
        event.id === editingEventId
          ? {
              ...event,
              startTime: startSeconds,
              endTime: endSeconds,
              duration: endSeconds - startSeconds,
            }
          : event
      );

    setRailEvents(updateEventInList(railEvents));

    setEditingEventId(null);
    setEditEventType(null);
    setEditTimeError('');
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setEditEventType(null);
    setEditTimeError('');
  };

  // Delete event handler
  const handleDeleteEvent = (eventId) => {
    setRailEvents(railEvents.filter((event) => event.id !== eventId));
  };

  // Submit all events handler
  const handleSubmitEvents = () => {
    if (!hasUnsavedChanges) {
      alert('No events to submit!');
      return;
    } else {
      const railsData = {
        rail: railEvents.reduce((events, event, index) => {
          events[`rail_${index + 1}`] = {
            type: event.type,
            start_time: event.startTime,
            end_time: event.endTime,
          };
          return events;
        }, {}),
      };

      const submitData = {
        driveId: driveId,
        driveName,
        rails: railsData,
        userId,
      };
      if (onEventsChange) {
        onEventsChange(submitData);
      }
      // Dispatch action to submit rail data to API
      dispatch(submitRailDataAction(submitData));
    }
  };

  // Combine all events for summary
  const allEvents = railEvents;
  const initialEventMap = new Map(initialRailEvents.map((event) => [event.id, event]));
  const addedEvents = railEvents.filter((event) => !initialEventMap.has(event.id));
  const deletedEvents = initialRailEvents.filter(
    (event) => !railEvents.some((currentEvent) => currentEvent.id === event.id)
  );
  const modifiedEvents = railEvents.filter((event) => {
    const initialEvent = initialEventMap.get(event.id);
    return (
      initialEvent &&
      JSON.stringify({
        type: event.type,
        startTime: event.startTime,
        endTime: event.endTime,
        height: event.height,
        postSpacing: event.postSpacing,
        deflection: event.deflection,
      }) !==
        JSON.stringify({
          type: initialEvent.type,
          startTime: initialEvent.startTime,
          endTime: initialEvent.endTime,
          height: initialEvent.height,
          postSpacing: initialEvent.postSpacing,
          deflection: initialEvent.deflection,
        })
    );
  });
  const hasUnsavedChanges =
    addedEvents.length > 0 || deletedEvents.length > 0 || modifiedEvents.length > 0;

  const EventListTable = ({ events, title }) => (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1, color: 'primary.default' }}>
        {title} ({events.length})
      </Typography>
      {editTimeError && editingEventId && (
        <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.7rem' }}>
          {editTimeError}
        </Alert>
      )}
      {events.length === 0 ? (
        <Typography
          sx={{ textAlign: 'center', color: 'text.secondary', py: 2, fontSize: '0.75rem' }}
        >
          No {title.toLowerCase()} recorded
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Start</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>End</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                  <TableCell sx={{ fontSize: '0.7rem' }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      {event.type}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem' }}>
                    {editingEventId === event.id ? (
                      <TextField
                        size="small"
                        value={editStartTime}
                        onChange={(e) => setEditStartTime(e.target.value)}
                        error={editTimeError !== ''}
                        placeholder="MM:SS"
                        helperText="MM:SS"
                        sx={{ width: '60px', '& input': { fontSize: '0.7rem', py: 0.5 } }}
                      />
                    ) : (
                      formatTime(event.startTime)
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem' }}>
                    {editingEventId === event.id ? (
                      <Box sx={{ fontWeight: 600 }}>
                        {formatTime(getCurrentVideoTime())} (current)
                      </Box>
                    ) : (
                      formatTime(event.endTime)
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>
                    {editingEventId === event.id ? (
                      <>
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            onClick={handleSaveEdit}
                            sx={{ color: 'success.main', p: 0.5 }}
                          >
                            <CheckIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                            sx={{ color: 'text.secondary', p: 0.5 }}
                          >
                            <CloseIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(event)}
                            sx={{ color: '#1976d2', p: 0.5 }}
                          >
                            <EditIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteEvent(event.id)}
                            sx={{ color: '#d32f2f', p: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  EventListTable.propTypes = {
    events: PropTypes.array.isRequired,
    title: PropTypes.string.isRequired,
  };

  return (
    <MainContainer>
      {/* CENTER - VIDEO PLAYER */}
      <VideoSection>
        <VideoPlayerCard>
          <VideoContainer>
            {/* RAIL TYPE BUTTONS OVERLAY — RIGHT */}
            <VideoOverlay position="right">
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  color: '#C8102E',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  mb: 0.5,
                  textAlign: 'center',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                RAIL TYPE
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarColor: 'rgba(255,255,255,0.3) transparent',
                  direction: 'rtl',
                }}
              >
                {railTypes.map((railType) => (
                  <Tooltip
                    key={railType.id}
                    title={
                      railType.shortcut
                        ? `${railType.name} (A+${railType.shortcut})`
                        : railType.name
                    }
                  >
                    <RailTypeButton
                      overlay
                      selected={recordingRailType?.id === railType.id}
                      isRecording={recordingRailType?.id === railType.id}
                      onClick={() => handleRailTypeClick(railType)}
                      variant="outlined"
                      size="small"
                      sx={{
                        backdropFilter: 'blur(8px)',
                        background: 'rgba(0,0,0,0.3)',
                        direction: 'ltr',
                      }}
                    >
                      {railType.name}
                    </RailTypeButton>
                  </Tooltip>
                ))}
              </Box>
            </VideoOverlay>

            {videoUrl ? (
              <Box
                ref={videoRef}
                component="video"
                src={videoUrl}
                controls
                controlsList="nodownload"
                style={{ width: '100%', height: '100%' }}
                onTimeUpdate={(e) => setLocalCurrentTime(e.currentTarget.currentTime)}
              />
            ) : (
              <Typography sx={{ color: '#999', textAlign: 'center', fontSize: '0.9rem' }}>
                🎬 No Video Available
                <br />
                <span
                  style={{ fontSize: '0.75rem', color: '#666', marginTop: '8px', display: 'block' }}
                >
                  Drive Name: {driveName}
                </span>
              </Typography>
            )}
          </VideoContainer>

          <VideoInfoBar>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    Current Time
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, fontFamily: 'monospace' }}>
                    {formatTime(localCurrentTime || currentTime)}
                  </Typography>
                </Box>
                <Tooltip title="Shortcut keys: A + 1..6">
                  <IconButton
                    size="small"
                    onClick={() => setShortcutModalOpen(true)}
                    sx={{ color: 'text.secondary', p: 0.5 }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {recordingRailType && (
                <RecordingIndicator isRecording>
                  <CircularProgress size={16} />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#d32f2f' }}>
                    Recording: {recordingRailType.name}
                  </Typography>
                </RecordingIndicator>
              )}
            </Box>
          </VideoInfoBar>
        </VideoPlayerCard>

        <Modal
          open={shortcutModalOpen}
          onClose={() => setShortcutModalOpen(false)}
          aria-labelledby="rail-shortcut-info-title"
          aria-describedby="rail-shortcut-info-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 340,
              maxHeight: '80vh',
              overflowY: 'auto',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 3,
              outline: 'none',
            }}
          >
            <Typography
              id="rail-shortcut-info-title"
              variant="h6"
              component="h2"
              sx={{ mb: 1, fontWeight: 700 }}
            >
              Shortcut Key Help
            </Typography>
            <Typography
              id="rail-shortcut-info-description"
              sx={{ mb: 2, fontSize: '0.9rem', color: 'text.secondary' }}
            >
              Use the keyboard shortcuts to start or stop rail-type recording quickly.
            </Typography>
            <Typography sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 700 }}>
              Left panel (press A, then a number)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
              {railTypes.map((railType) => (
                <Typography key={railType.id} sx={{ fontSize: '0.85rem' }}>
                  • Press <strong>A</strong> + <strong>{railType.shortcut}</strong> for{' '}
                  {railType.name}
                </Typography>
              ))}
            </Box>
            <Button variant="contained" fullWidth onClick={() => setShortcutModalOpen(false)}>
              Close
            </Button>
          </Box>
        </Modal>
      </VideoSection>

      {/* RIGHT PANEL - EVENTS LIST WITH SUBMIT BUTTON */}
      <RightPanel>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            gap: 1.5,
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              boxShadow: 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.9rem',
                letterSpacing: 0.5,
              }}
            >
              ALL EVENTS
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
              }}
            >
              {allEvents.length} recorded
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: '0.75rem',
                color: hasUnsavedChanges ? 'warning.dark' : 'text.secondary',
              }}
            >
              {hasUnsavedChanges
                ? `${addedEvents.length} ${addedEvents.length === 1 ? 'event' : 'events'} added, ${deletedEvents.length} ${deletedEvents.length === 1 ? 'event' : 'events'} deleted${modifiedEvents.length > 0 ? `, ${modifiedEvents.length} modified` : ''}`
                : 'No changes to submit'}
            </Typography>
          </Box>

          {/* LIST */}
          <DataListCard
            sx={{
              flex: 1,
              borderRadius: 2,
              boxShadow: 2,
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              backgroundColor: 'background.paper',
            }}
          >
            <EventListTable events={railEvents} title="Recorded rail events" />

            {allEvents.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  borderRadius: 2,
                  backgroundColor: 'grey.50',
                }}
              >
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    fontStyle: 'italic',
                  }}
                >
                  Click a rail type on the video to start recording.
                </Typography>
              </Box>
            )}
          </DataListCard>
        </Box>

        {/* ALERTS */}
        {showErrorAlert && submitError && (
          <Alert
            severity="error"
            sx={{
              fontSize: '0.8rem',
              borderRadius: 2,
            }}
          >
            {submitError}
          </Alert>
        )}

        {showSuccessAlert && submitStatus === RequestStatus.SUCCEEDED && (
          <Alert
            severity="success"
            sx={{
              fontSize: '0.8rem',
              borderRadius: 2,
            }}
          >
            Rail markings submitted successfully!
          </Alert>
        )}

        {/* BUTTON */}
        <Button
          onClick={handleSubmitEvents}
          variant="contained"
          fullWidth
          disabled={!hasUnsavedChanges || submitStatus === RequestStatus.LOADING}
          sx={{
            mt: 1,
            py: 1.6,
            fontSize: '0.9rem',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 2,
            transition: 'all 0.2s ease',

            background:
              hasUnsavedChanges && submitStatus !== RequestStatus.LOADING
                ? 'linear-gradient(135deg, #5f9cff 0%, #7b5cff 100%)'
                : 'grey.400',

            '&:hover': {
              transform: submitStatus !== RequestStatus.LOADING ? 'translateY(-1px)' : 'none',
              boxShadow: submitStatus !== RequestStatus.LOADING ? 6 : 2,
            },

            '&:disabled': {
              opacity: 0.6,
            },
          }}
        >
          {submitStatus === RequestStatus.LOADING ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
              Submitting...
            </Box>
          ) : submitStatus === RequestStatus.SUCCEEDED ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ fontSize: '1.2rem' }} />
              Submitted
            </Box>
          ) : (
            'Submit Events'
          )}
        </Button>
      </RightPanel>
    </MainContainer>
  );
};

RailMarkingRecorder.propTypes = {
  driveId: PropTypes.string,
  driveName: PropTypes.string,
  videoUrl: PropTypes.string,
  currentTime: PropTypes.number,
  userId: PropTypes.string,
  onEventsChange: PropTypes.func,
};

RailMarkingRecorder.defaultProps = {
  driveId: null,
  driveName: 'demo-drive',
  videoUrl: null,
  currentTime: 0,
  userId: null,
  onEventsChange: null,
};

export default RailMarkingRecorder;
